import { EventDispatcher } from './eventdispatcher';
import { v4 as uuidv4 } from 'uuid';
import { Loggy } from './loggy';
import PubSub from './client/pubsub/pubsub';
import RPC from './client/rpc/rpc';

/**
 * Master-mux client — one raw WebSocket to `/v1/mux/ws`, multiplexed over
 * named channels. Wire protocol matches `backend/master/mux/envelope.go`:
 *
 *   Auth (first frame, client → server):
 *     { ch:"_mux", evt:"hello", token:"<bearer>" }
 *
 *   Server ready frame:
 *     { ch:"_mux", evt:"ready", channels:["mail","calendar","chat","agent"] }
 *
 *   Data on a channel (both directions):
 *     { ch:"agent", msg:{ ...per-channel inner protocol... } }
 *
 *   Master LocalMode push (server → browser, no upstream module):
 *     { ch:"master", evt:"notification.new", msg:{ ...payload... } }
 *   The evt namespaces the push kind so the one "master" channel can carry
 *   notifications and future server-originated pushes side by side.
 *
 * Sub-apps use `client.channel('agent')` (or `useNxSocket().channel(...)`)
 * to get a per-channel sender/listener. Inner messages are passed through
 * untouched — each channel defines its own sub-protocol.
 *
 * Heartbeat + reconnect logic mirror the legacy connect/ socket port. The
 * PubSub/RPC helpers from that port are kept as no-op shims so any existing
 * imports continue to load (the new transport doesn't carry them).
 */
export default class Client extends EventDispatcher {
  ws: WebSocket | null = null;
  url: string;
  name: string;
  greeted: boolean;
  connected: boolean;
  debug: boolean;
  reconnect: boolean;
  log: Loggy;

  appKey: string;
  appVersion: string;
  bearer: string;

  // Optional callback the shell sets so the socket re-reads the *current*
  // bearer on every (re)connect instead of a value captured once at page
  // load. Without it the socket would keep handshaking with a token that the
  // OAuth refresh path has long since rotated — the "stays red until reload"
  // bug. Falls back to the cached `bearer` when unset.
  bearerProvider: (() => string) | null = null;
  private handshakeBearer = '';

  // Optional callback that yields the URL to dial, read on every connect() so
  // the socket always targets the API origin the HTTP client uses right now —
  // never a default captured before the user chose a server (I-2833).
  urlProvider: (() => string) | null = null;
  // Optional callback that yields the API origin which issued the bearer. The
  // hello frame is only sent when the dialed origin matches it, so a token
  // never travels to a server other than the one that minted it.
  bearerOriginProvider: (() => string) | null = null;
  // The URL the current `ws` was opened with ('' while there is none).
  private dialedUrl = '';

  // Legacy helpers from the old `/echo` socket port. Retained so imports of
  // NxPubSub / NxRPC don't break, but no longer reachable through the master
  // mux protocol.
  pubsub: PubSub;
  rpc: RPC;

  // Mux state
  private channelMap: Map<string, MuxChannel> = new Map();
  private ready: boolean = false;
  private availableChannels: string[] = [];
  private outboundQueue: Envelope[] = [];

  lastPing: number = 0;
  currLatency: number = 0;
  pingTimer: ReturnType<typeof setTimeout> | null = null;
  timeout: ReturnType<typeof setTimeout> | null = null;

  // Reconnect backoff: 1s, 2s, 4s … capped at 30s, with ±20% jitter so many
  // tabs don't reconnect in lockstep and hammer a just-restarted server.
  // Reset to 0 once the mux handshake reaches `ready`.
  private reconnectAttempts: number = 0;
  private static readonly RECONNECT_BASE_MS = 1000;
  private static readonly RECONNECT_MAX_MS = 30000;

  private static instance: Client;

  public static setInstance(c: Client) {
    Client.instance = c;
  }

  public static getInstance(): Client {
    return Client.instance;
  }

  uuid: string;

  constructor(url: string) {
    super();
    this.name = '';
    this.url = url;
    this.uuid = uuidv4().toString();
    this.log = new Loggy();
    this.greeted = false;
    this.connected = false;
    this.debug = true;
    this.reconnect = true;
    this.appKey = '';
    this.appVersion = '';
    this.bearer = '';
    this.pubsub = new PubSub(this);
    this.rpc = new RPC(this);
  }

  setName(name: string) {
    this.name = name;
  }

  /** Set the OAuth bearer used in the `_mux` hello frame. Call before `connect()`. */
  setBearer(token: string) {
    this.bearer = token;
  }

  /**
   * Register a provider that yields the *current* bearer at connect time.
   * Preferred over `setBearer` for the shared shell socket: it is read fresh
   * inside `onOpen`, so a reconnect after a silent token refresh handshakes
   * with the rotated token instead of the stale one captured at page load.
   */
  setBearerProvider(fn: () => string) {
    this.bearerProvider = fn;
  }

  /** Register a provider for the URL to dial; read fresh on every connect(). */
  setUrlProvider(fn: () => string) {
    this.urlProvider = fn;
  }

  /** Register a provider for the API origin that issued the current bearer. */
  setBearerOriginProvider(fn: () => string) {
    this.bearerOriginProvider = fn;
  }

  /** The URL the next connect() dials: the provider's answer, else `url`. */
  private resolveUrl(): string {
    if (this.urlProvider) {
      const url = this.urlProvider();
      if (url) this.url = url;
    }
    return this.url;
  }

  /**
   * True when the open (or opening) socket targets the origin the shell uses
   * now. A socket dialed before the API base changed is foreign: nothing it
   * says about authentication concerns the current session.
   */
  private onCurrentOrigin(): boolean {
    if (!this.dialedUrl) return true;
    return sameOrigin(this.dialedUrl, this.resolveUrl());
  }

  /**
   * Move the socket to the current URL when it is open (or opening) on a
   * different one: the old socket is closed first, then the current URL is
   * dialed (connect() still refuses without a session). Returns true when it
   * redialed.
   */
  retarget(): boolean {
    if (!this.ws || this.dialedUrl === this.resolveUrl()) return false;
    this.stop();
    this.reconnectAttempts = 0;
    this.connect();
    return true;
  }

  /**
   * Reopen the mux after a token refresh. A re-hello only changes master's
   * bearer for FUTURE channel dials; existing upstream sockets keep the
   * Authorization header from their handshake and would still expire.
   */
  reauth(): boolean {
    if (!this.isReady()) return false;
    this._close();
    this.reconnectNow();
    return true;
  }

  /** Wait for the fresh handshake before retrying an explicitly rejected call. */
  waitUntilReady(timeoutMs = 30_000): Promise<boolean> {
    if (this.isReady()) return Promise.resolve(true);
    return new Promise((resolve) => {
      const finish = (ready: boolean) => {
        clearTimeout(timer);
        offReady();
        offAuthFailed();
        resolve(ready);
      };
      const offReady = this.on('ready', () => finish(true));
      const offAuthFailed = this.on('auth-failed', () => finish(false));
      const timer = setTimeout(() => finish(false), timeoutMs);
    });
  }

  /** Resolve the bearer to send in the hello frame — provider wins, cache is the fallback. */
  private resolveBearer(): string {
    if (this.bearerProvider) {
      const token = this.bearerProvider();
      if (token) {
        this.bearer = token;
        return token;
      }
    }
    return this.bearer;
  }

  /** Get or create a per-channel envelope (Agent, Mail, Chat, ...). */
  channel(name: string): MuxChannel {
    let ch = this.channelMap.get(name);
    if (!ch) {
      ch = new MuxChannel(this, name);
      this.channelMap.set(name, ch);
    }
    return ch;
  }

  /** Returns the channel names the server advertised in its `ready` frame. */
  channels(): string[] {
    return [...this.availableChannels];
  }

  isReady(): boolean {
    return this.connected && this.ready;
  }

  getRpc(): RPC {
    return this.rpc;
  }

  getPubSub(): PubSub {
    return this.pubsub;
  }

  sendPing() {
    if (!this.connected) return;
    this.lastPing = Date.now();
    // Mux protocol has no app-level ping; the WebSocket layer itself takes
    // care of TCP-level keepalive. We keep the timer for symmetry with the
    // legacy code so latency self-checks remain available if the server
    // ever adds a ping channel.
    this.pingTimer = setTimeout(() => this.sendPing(), 30000);
  }

  connect() {
    if (this.connected) return;
    // Guard against a socket that's already mid-handshake (CONNECTING) or open
    // but not yet flagged connected — a manual connect() (Settings dialog) or a
    // racing reconnect timer must not spawn a second, orphaned WebSocket.
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) return;
    // The mux authenticates every socket; without a session (the login page)
    // there is nothing to say, so no socket at all. The shell's auth binding
    // dials once a login stores a bearer. Only a client wired to a bearer
    // provider knows whether there is a session.
    if (this.bearerProvider && !this.resolveBearer()) {
      if (this.debug) this.log.info('mux: no session, not connecting');
      return;
    }
    const url = this.resolveUrl();
    if (this.debug) this.log.info('mux: connecting', url);
    this.reconnect = true;
    try {
      // The master mux upgrader requires the 'formica-mux' subprotocol;
      // without it the handshake returns HTTP 400. Keep in sync with
      // `Subprotocol` in backend/master/mux/server.go.
      this.ws = new WebSocket(url, ['formica-mux']);
      this.dialedUrl = url;
    } catch (err) {
      this.log.error('mux: WebSocket construction failed', err);
      this.scheduleReconnect();
      return;
    }
    this.ws.onopen = this.onOpen.bind(this);
    this.ws.onmessage = this.onMessage.bind(this);
    this.ws.onerror = this.onError.bind(this);
    this.ws.onclose = this.onClose.bind(this);
  }

  private onOpen() {
    // The API base moved while this socket was dialing: it reached a server
    // the session no longer talks to. Never hand it the bearer — close it and
    // dial the current origin instead.
    if (!this.onCurrentOrigin()) {
      this.log.info('mux: socket open on a stale origin, redialing', this.dialedUrl);
      this.retarget();
      return;
    }
    // Read the bearer fresh so a reconnect uses the current (possibly
    // refreshed) token, never the one captured at page load.
    const bearer = this.resolveBearer();
    const issuer = this.bearerOriginProvider?.() ?? '';
    if (this.bearerProvider && !bearer) {
      // Signed out while dialing: nothing to authenticate.
      this.log.info('mux: no session, closing');
      this.stop();
      return;
    }
    if (bearer && issuer && !sameOrigin(issuer, this.dialedUrl)) {
      // A token is only ever sent to the origin that issued it.
      this.log.error('mux: bearer was issued by another origin, not sending it', this.dialedUrl);
      this.stop();
      return;
    }
    this.connected = true;
    this.ready = false;
    this.log.info('mux: socket open', this.dialedUrl);
    this.dispatch('connected', this);
    // Auth in-band: hello frame must be the FIRST message after handshake.
    this.handshakeBearer = bearer;
    this.sendEnvelope({ ch: '_mux', evt: 'hello', token: this.handshakeBearer });
    this.sendPing();
  }

  private onMessage(e: MessageEvent) {
    try {
      const env = JSON.parse(typeof e.data === 'string' ? e.data : '') as Envelope;
      if (!env || typeof env.ch !== 'string') return;
      if (env.ch === '_mux') {
        this.handleSystemFrame(env);
        return;
      }
      const ch = this.channelMap.get(env.ch);
      // evt takes precedence over msg: a frame that carries evt is either a
      // proxied channel lifecycle event (open/close, no msg) or a master
      // LocalMode push that namespaces its kind in evt AND ships a msg
      // payload (e.g. {evt:"notification.new", msg:…}). Routing it to
      // handleEvent hands subscribers the whole envelope so they can switch
      // on evt and still read msg. Plain proxied data frames carry msg only
      // and fall through to handleData unchanged.
      if (env.evt) {
        ch?.handleEvent(env);
        if (env.evt === 'open') this.dispatch('channel-open', env.ch);
        if ((env.evt === 'error' && env.code === 401) || (env.evt === 'close' && env.code === 4401)) {
          if (this.ignoreForeignRefusal()) return;
          this.dispatch('authentication-failed', { scope: env.ch, bearer: this.handshakeBearer });
        }
      } else if (env.msg !== undefined) {
        ch?.handleData(env.msg);
      }
    } catch (err) {
      this.log.error('mux: bad frame', err);
    }
  }

  private handleSystemFrame(env: Envelope) {
    if (env.evt === 'ready') {
      this.ready = true;
      this.reconnectAttempts = 0; // handshake succeeded — reset backoff
      this.availableChannels = env.channels ?? [];
      this.log.info('mux: ready', this.availableChannels.join(','));
      this.dispatch('ready', this.availableChannels);
      this.flushQueue();
    } else if (env.evt === 'reauthed') {
      // Live token rebind succeeded — the connection kept running, no reconnect.
      this.log.info('mux: reauthed');
      this.dispatch('reauthed', this);
    } else if (env.evt === 'auth_failed') {
      // The mux rejected our hello token (expired/invalid). The server closes
      // the socket right after, so onClose will reconnect — but with the SAME
      // token unless something refreshes it first. Surface this so the shell
      // can trigger an OAuth refresh; the reconnect then rides the new bearer.
      this.log.error('mux: auth failed', env.reason);
      if (this.ignoreForeignRefusal()) return;
      this.dispatch('auth-failed', env);
      this.dispatch('authentication-failed', { scope: '_mux', bearer: this.handshakeBearer });
    } else if (env.evt === 'error') {
      this.log.error('mux: server error', env.code, env.reason);
      this.dispatch('mux-error', env);
    } else if (env.evt === 'pong') {
      this.currLatency = Math.abs(this.lastPing - Date.now());
    }
  }

  /**
   * An auth refusal from a socket that is not on the current API origin says
   * nothing about the session — it must never sign the user out. Redial the
   * current origin instead and report the refusal as handled.
   */
  private ignoreForeignRefusal(): boolean {
    if (this.onCurrentOrigin()) return false;
    this.log.info('mux: ignoring auth refusal from a foreign origin', this.dialedUrl);
    this.retarget();
    return true;
  }

  private onError(e: Event) {
    this.log.error('mux: onError', e);
    this.dispatch('error', e);
  }

  private onClose(e: CloseEvent) {
    this.log.error('mux: onClose', e?.code, e?.reason);
    if (e?.code === 4401) {
      // retarget() already replaced the foreign socket — do not close the new one.
      if (this.ignoreForeignRefusal()) return;
      this.dispatch('authentication-failed', { scope: '_mux', bearer: this.handshakeBearer });
    }
    this._close();
  }

  private _close() {
    if (this.ws) {
      // A delayed close/message from the old connection must not tear down
      // its replacement or resolve requests against the old authenticated socket.
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      try {
        this.ws.close();
      } catch {
        // already closed
      }
    }
    this.ws = null;
    this.dialedUrl = '';
    this.greeted = false;
    this.connected = false;
    this.ready = false;
    this.dispatch('disconnected', this);
    if (this.pingTimer) {
      clearTimeout(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.reconnect) this.scheduleReconnect();
  }

  private scheduleReconnect() {
    if (this.timeout) clearTimeout(this.timeout);
    // Exponential backoff with ±20% jitter, capped at RECONNECT_MAX_MS.
    const exp = Math.min(
      Client.RECONNECT_MAX_MS,
      Client.RECONNECT_BASE_MS * 2 ** this.reconnectAttempts,
    );
    const jitter = exp * 0.2 * (Math.random() * 2 - 1);
    const delay = Math.max(Client.RECONNECT_BASE_MS, Math.round(exp + jitter));
    this.reconnectAttempts++;
    if (this.debug) this.log.info('mux: reconnect in', delay, 'ms', `(attempt ${this.reconnectAttempts})`);
    this.timeout = setTimeout(() => {
      if (!this.connected) this.connect();
    }, delay);
  }

  /**
   * Reconnect immediately, bypassing the backoff timer. Used by the shell when
   * the OAuth token was refreshed and the socket is currently down — no point
   * waiting out the backoff with a now-fixable (fresh) bearer.
   */
  reconnectNow() {
    this.reconnectAttempts = 0;
    if (this.isReady()) {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
      return; // already up and handshaken — nothing to do
    }
    this.reconnect = true;
    // Tear a half-open/stale socket down first. _close() itself schedules a
    // backoff reconnect, so cancel that timer afterwards and connect now.
    if (this.connected) this._close();
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    this.connect();
  }

  stop() {
    this.reconnect = false;
    if (this.timeout) clearTimeout(this.timeout);
    this._close();
  }

  isConnected(): boolean {
    return this.connected;
  }

  /**
   * Send raw `msg` JSON on a named channel. Auto-queues if the socket isn't
   * `ready` yet (during initial handshake) and flushes after `ready`.
   */
  sendOnChannel(channel: string, msg: unknown) {
    const env: Envelope = { ch: channel, msg: msg as never };
    if (!this.isReady()) {
      this.outboundQueue.push(env);
      return;
    }
    this.sendEnvelope(env);
  }

  private flushQueue() {
    while (this.outboundQueue.length > 0) {
      const env = this.outboundQueue.shift()!;
      this.sendEnvelope(env);
    }
  }

  private sendEnvelope(env: Envelope) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(env));
    } catch (err) {
      this.log.error('mux: send failed', err);
    }
  }

  // Legacy compat shims so old call-sites that did sendObject({...}) don't
  // throw; they now route the same object through the legacy `_legacy`
  // channel which the master mux will reject — surfacing the migration.
  sendData(_data: string) {
    /* kept for source-compat with the old port */
  }
  sendString(data: string) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(data);
  }
  sendObject(obj: unknown) {
    this.sendString(JSON.stringify(obj));
  }
}

/** True when two http(s)/ws(s) URLs share scheme family, host and port. */
export function sameOrigin(a: string, b: string): boolean {
  const origin = (url: string) => {
    try {
      return new URL(url.replace(/^ws/i, 'http')).origin;
    } catch {
      return url;
    }
  };
  return origin(a) === origin(b);
}

interface Envelope {
  ch: string;
  id?: string;
  msg?: unknown;
  evt?: string;
  code?: number;
  reason?: string;
  channels?: string[];
  token?: string;
}

/**
 * A per-channel sender/listener handed out by `client.channel(name)`.
 * Subscribers receive the channel's inner `msg` payloads via `on('data', …)`
 * and lifecycle events via `on('event', …)`.
 */
export class MuxChannel extends EventDispatcher {
  client: Client;
  name: string;
  constructor(client: Client, name: string) {
    super();
    this.client = client;
    this.name = name;
  }
  send(msg: unknown) {
    this.client.sendOnChannel(this.name, msg);
  }
  handleData(msg: unknown) {
    this.dispatch('data', msg);
  }
  handleEvent(env: Envelope) {
    this.dispatch('event', env);
  }
}
